import { CalendarIcon, DownloadIcon, Search2Icon, TriangleUpIcon, TriangleDownIcon, UpDownIcon } from "@chakra-ui/icons";
import { TableContainer, Table, Thead, Tr, Th, Tbody, Td, Box, InputGroup, InputRightElement, Badge, Center, Button, Select, Input, Checkbox, Stack, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.min.css';
import { ManagerLeaveHelper } from "../../dal/manager_leave_helper";
import { SchoolDateHelper } from "../../dal/school_date_helper";
import { SchoolPeriodHelper } from "../../dal/school_period_helper";
import { AbsenceRecordInfo, LeaveApplicationRecord, LeaveDateAndPeriod, SchoolDateConfig } from "../../dal/value_object";
import { DSAAbsenceHelper } from "../../dal/dsa_absence_helper";
import zhTW from "date-fns/locale/zh-TW";
import { LeaveAppModal } from "../../components/Modals/LeaveAppModal";
import Excel from 'exceljs';
import FileSaver from 'file-saver';
import dayjs from 'dayjs';
import { Util } from "../../dal/util";
import { useUserContext } from "../../components/auth/user-validation"
import { useSchoolContext } from "../../dal/SchoolContext";
import { LoadingDialog } from "../../components/LoadingDialog";

registerLocale('zhTW', zhTW);

interface sortParameter {
  key: string;
  sortType: number;    // 0: no sort, 1: ascending, -1: descending
}

interface StudentAbsenceMatchInfo {
  studentID: number;
  absenceRecords: AbsenceRecordInfo[];
  matchedAbsenceRecords: AbsenceRecordInfo[];
  isMatched: boolean;
  compareRequired: boolean;
}

interface LeaveApplicationRecordWithAbsence extends LeaveApplicationRecord {
  studentAbsenceMatchInfo: StudentAbsenceMatchInfo[];
  isAbsenceRecordMatched: boolean;
}

const getStudentAbsenceMatchInfo = (
  leaveApp: LeaveApplicationRecordWithAbsence,
  studentID: number
) => leaveApp.studentAbsenceMatchInfo.find((item) => item.studentID === studentID);

const dedupePreserveOrder = (items: string[]) => Array.from(new Set(items));

const sortPeriodsByPeriodNames = (periods: string[], periodNames: string[]) => {
  const periodOrderMap = new Map(periodNames.map((period, index) => [period, index]));

  return dedupePreserveOrder([...periods]).sort((left, right) => {
    const leftOrder = periodOrderMap.get(left) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = periodOrderMap.get(right) ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return left.localeCompare(right, 'zh-Hant');
  });
};

const sortLeaveDateAndPeriodByPeriodNames = (leaveDateAndPeriod: LeaveDateAndPeriod[], periodNames: string[]) => (
  leaveDateAndPeriod.map((item) => ({
    ...item,
    periods: sortPeriodsByPeriodNames(item.periods, periodNames)
  }))
);

const sortAbsenceRecordsByPeriodNames = (absenceRecords: AbsenceRecordInfo[], periodNames: string[]) => {
  const periodOrderMap = new Map(periodNames.map((period, index) => [period, index]));

  return absenceRecords.map((record) => ({
    ...record,
    AbsenceDetail: [...record.AbsenceDetail].sort((left, right) => {
      const leftOrder = periodOrderMap.get(left.Period) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = periodOrderMap.get(right.Period) ?? Number.MAX_SAFE_INTEGER;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      if (left.Period !== right.Period) {
        return left.Period.localeCompare(right.Period, 'zh-Hant');
      }

      return left.AbsenceType.localeCompare(right.AbsenceType, 'zh-Hant');
    })
  }));
};

const buildLeavePeriodMap = (leaveDateAndPeriod: LeaveDateAndPeriod[]) => {
  const leavePeriodMap = new Map<string, string[]>();

  leaveDateAndPeriod.forEach(({ d, periods }) => {
    const currentPeriods = leavePeriodMap.get(d) ?? [];
    leavePeriodMap.set(d, dedupePreserveOrder([...currentPeriods, ...periods]));
  });

  return leavePeriodMap;
};

const buildAbsenceDetailMap = (absenceRecords: AbsenceRecordInfo[]) => {
  const absenceDetailMap = new Map<string, string[]>();

  absenceRecords.forEach((record) => {
    const matchedDetails = record.AbsenceDetail
      .map((detail) => `${detail.Period}|${detail.AbsenceType}`);

    if (matchedDetails.length === 0) {
      return;
    }

    const currentDetails = absenceDetailMap.get(record.OccurDate) ?? [];
    absenceDetailMap.set(record.OccurDate, dedupePreserveOrder([...currentDetails, ...matchedDetails]));
  });

  return absenceDetailMap;
};

const formatAbsenceDetail = (details: string[]) => {
  if (details.length === 0) {
    return '無';
  }

  return details
    .map((detail) => {
      const [period, absenceType] = detail.split('|');
      return `${period}(${absenceType})`;
    })
    .join('、');
};

const getExportAbsenceInfo = (
  leaveDateAndPeriod: LeaveDateAndPeriod | undefined,
  absenceType: string,
  studentAbsenceMatchInfo: StudentAbsenceMatchInfo | undefined
) => {
  if (!leaveDateAndPeriod || !studentAbsenceMatchInfo?.compareRequired) {
    return {
      schoolAbsenceDetail: '',
      absenceComparison: '',
    };
  }

  const absenceDetailMap = buildAbsenceDetailMap(studentAbsenceMatchInfo.matchedAbsenceRecords);
  const expectedDetails = dedupePreserveOrder(
    leaveDateAndPeriod.periods.map((period) => `${period}|${absenceType}`)
  );
  const actualDetails = absenceDetailMap.get(leaveDateAndPeriod.d) ?? [];
  const isMatched = expectedDetails.length > 0
    && expectedDetails.every((detail: string) => actualDetails.includes(detail));

  return {
    schoolAbsenceDetail: isMatched ? '' : formatAbsenceDetail(actualDetails),
    absenceComparison: isMatched ? '一致' : '不一致',
  };
};

const isAbsenceRecordMatched = (
  leaveDateAndPeriod: LeaveDateAndPeriod[],
  absenceType: string,
  absenceRecords: AbsenceRecordInfo[]
) => {
  const leavePeriodMap = buildLeavePeriodMap(leaveDateAndPeriod);
  const leaveDetailMap = new Map(
    Array.from(leavePeriodMap.entries()).map(([date, periods]) => [
      date,
      dedupePreserveOrder(periods.map((period) => `${period}|${absenceType}`))
    ])
  );
  const absenceDetailMap = buildAbsenceDetailMap(absenceRecords);

  // 檢查請假單的每個日期和節次是否都在校務缺曠中（校務缺曠可以有額外的節次）
  return Array.from(leaveDetailMap.entries()).every(([date, leaveDetails]) => {
    const absenceDetails = absenceDetailMap.get(date);
    if (!Array.isArray(absenceDetails)) {
      return false; // 該日期在校務缺曠中不存在
    }
    // 檢查請假單的每個節次+類型是否都在校務缺曠中
    return leaveDetails.every((detail: string) => absenceDetails.includes(detail));
  });
};

const getMatchedAbsenceRecords = (
  leaveDateAndPeriod: LeaveDateAndPeriod[],
  absenceRecords: AbsenceRecordInfo[]
) => {
  const leaveDates = new Set(leaveDateAndPeriod.map((item) => item.d));

  return absenceRecords.filter((record) => leaveDates.has(record.OccurDate));
};

const formatApproveStatus = (status?: string) => {
  if (status === 'approved') {
    return '核准';
  }

  if (status === 'rejected') {
    return '退回';
  }

  return '';
};

const buildApproveHistorySummary = (approveHistory: LeaveApplicationRecord['approveHistory'] = []) => ({
  approve_status: approveHistory.map((item) => formatApproveStatus(item.status)).filter(Boolean).join('\n'),
  approve_time: approveHistory
    .map((item) => item.approve_time ? dayjs(item.approve_time).format('YYYY/MM/DD HH:mm:ss') : '')
    .filter(Boolean)
    .join('\n'),
  approver: approveHistory.map((item) => item.approver_name ?? '').filter(Boolean).join('\n'),
  comment: approveHistory.map((item) => item.comment ?? '').filter(Boolean).join('\n')
});

const getExcelColumnName = (columnNumber: number) => {
  let result = '';
  let current = columnNumber;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    current = Math.floor((current - 1) / 26);
  }

  return result;
};

/* 查詢全校請假紀錄 */
export const RecordPage = () => {

  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [statusArr, setStatusArr] = useState([true, true, true, true]); // 審核狀態字串陣列

  const color = useColorModeValue('gray.600', 'gray.400');

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const queryDateRangeRef = useRef({ startDate: new Date(), endDate: new Date() });

  const [LeaveAppAllList, setLeaveAppAllList] = useState<LeaveApplicationRecordWithAbsence[]>([]); // 從資料庫中取得的紀錄
  const [LeaveAppList, setLeaveAppList] = useState<LeaveApplicationRecordWithAbsence[]>([]); // 篩選後的紀錄  
  const [runFilter, setRunFilter] = useState(false); // 重新篩選紀錄
  const [allClasses, setAllClasses] = useState<any[]>([]); // 所有班級資料
  const [gradeYears, setGradeYears] = useState<number[]>([]); // 儲存不重複年級
  const [gradeClass, setGradeClass] = useState<any[]>([]); // 年級班級資料
  const [gradeYear, setGradeYear] = useState<number>(0); // 年級
  const [currentClass, setCurrentClass] = useState<number>(0); // 班級ID
  const [leaveAppNo, setLeaveAppNo] = useState<string>(''); // 請假單號 application_no
  const [keyword, setKeyword] = useState(''); // 姓名或學號關鍵字
  const [keyDays, setKeyDays] = useState<number | null>(null); // 請假日期的最後一天和申請日期比的天數(日曆天)
  const [showLoading, setShowLoading] = useState<boolean>(false); // 是否顯示載入中圖示的視窗

  const [donwloadEnable, setDownloadEnable] = useState(false); // 下載按鈕是否啟用

  const [selectedLeaveApp, setSelectedLeaveApp] = useState<any>(); // 選擇的請假紀錄
  const [selectedLeaveStu, setSelectedLeaveStu] = useState<any>(); // 選擇的請假學生

  const [sortParameters, setSortParameters] = useState<sortParameter[]>([  // 排序參數
    { key: 'leaveStudents[0].gradeYear', sortType: 0 },
    { key: 'leaveStudents[0].className', sortType: 0 },
    { key: 'leaveStudents[0].studentNumber', sortType: 0 },
    { key: 'leaveStudents[0].name', sortType: 0 },
    { key: 'leaveDateAndPeriod.d[0]', sortType: 0 },
  ]);

  const { userInfo } = useUserContext(); // 使用者資訊

  const { selectedSchool } = useSchoolContext(); // 學校資訊
  const [schoolDatesDetail, setSchoolDatesDetail] = useState<SchoolDateConfig[]>([]); // 學校在所有學期定義的上課日詳細資訊
  const selectedDsns = selectedSchool?.dsns ?? '';

  const handleDetailOpen = (item: any, student: any) => () => {
    setSelectedLeaveApp(item);
    setSelectedLeaveStu(student);
    onModalOpen();
  }

  const handleStatus = (index: number, chk: boolean) => {
    const temp = [...statusArr];
    temp[index] = chk;
    setStatusArr(temp);
    setRunFilter(true);
  }

  const handleKeyword = () => (e: any) => {
    setKeyword(e.target.value);
    setRunFilter(true);
  }
  const handleKeyDays = () => (e: any) => {
    const value = e.target.value;

    if (value.trim() === '') {
      setKeyDays(null);
      setRunFilter(true);
      return;
    }

    // 檢查正整數
    if (/^\d+$/.test(value)) {
      const num = parseInt(value, 10);

      if (num > 0) {
        setKeyDays(num);
        setRunFilter(true);
      } else {
        // 非正整數
        setKeyDays(null);
      }
    } else {
      // 非數字
      setKeyDays(null);
    }
  }
  const handleGradeYear = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    console.log('handleGradeYear', value);

    // 選回預設的「年級」(value='0') 或空值時
    if (value === '' || value === '0') {
      setGradeYear(0);
      setGradeClass([]); // 或者恢復為全部班級 allClasses
    } else {
      const intGrade = parseInt(value, 10);
      setGradeYear(intGrade);

      // 使用 Number() 轉換，避免 API 回傳的 gradeYear 是字串 (例如 "1") 導致 === 比對失敗
      setGradeClass(allClasses.filter((item) => Number(item.gradeYear) === intGrade));
    }

    setCurrentClass(0);
    setRunFilter(true);
  };

  const handleCurrentClass = () => (e: any) => {
    if (e.target.value === '') {
      setCurrentClass(0);
    }
    else {
      const intClass = parseInt(e.target.value);
      setCurrentClass(intClass);
    }
    setRunFilter(true);
  }

  const updateSortParameters = (idx: number) => {
    if (sortParameters[idx].sortType === 0) {
      setSortParameters(sortParameters.map((item, index) => {
        if (index === idx) return { key: item.key, sortType: 1 };
        return item;
      }));
    }
    else if (sortParameters[idx].sortType === 1) {
      setSortParameters(sortParameters.map((item, index) => {
        if (index === idx) return { key: item.key, sortType: -1 };
        return item;
      }));
    }
    else if (sortParameters[idx].sortType === -1) {
      setSortParameters(sortParameters.map((item, index) => {
        if (index === idx) return { key: item.key, sortType: 0 };
        return item;
      }));
    }
    setRunFilter(true);
  }

  // 轉換請假字串
  const convLeaveDateStr = (leaveDateAndPeriod: any) => {
    let result = dayjs(leaveDateAndPeriod[0].d).format('YYYY/MM/DD');
    if (leaveDateAndPeriod.length > 1) {
      result += "~" + dayjs(leaveDateAndPeriod[leaveDateAndPeriod.length - 1].d).format('MM/DD') + "(" + leaveDateAndPeriod.length + "天)";
    }
    return result;
  }

  const getLeaveRecord = useCallback(async (dsns: string, queryStartDate: Date, queryEndDate: Date) => {
    if (userInfo && dsns) {
      console.log('===開始取得請假紀錄===', { userInfo, dsns, queryStartDate, queryEndDate });
      setShowLoading(true);
      try {
        const LeaveApplicationList = await ManagerLeaveHelper.get_by_leave_date(
          queryStartDate.toDateString(),
          queryEndDate.toDateString(),
          dsns
        );

        const shouldCompareAbsence = !!userInfo?.isSystemAdmin;
        const finishedLeaveApplicationList = shouldCompareAbsence
          ? LeaveApplicationList.filter((item: LeaveApplicationRecord) => item.status === 'finished')
          : [];

        const absenceRequestKeys = Array.from(new Set(
          finishedLeaveApplicationList.flatMap((item: LeaveApplicationRecord) => (
            item.leaveStudents.map((student) => `${item.school_year}_${item.semester}_${student.studentID}`)
          ))
        ));

        // 取得學校允許請假的節次，用於排序請假節次與缺曠節次
        const periodName = await SchoolPeriodHelper.getAllowedPeriods(undefined, dsns);

        const absenceRecordEntries = await Promise.all(
          absenceRequestKeys.map(async (key) => {
            const [schoolYear, semester, studentID] = key.split('_').map(Number);
            const absenceRecords = await DSAAbsenceHelper.getAbsenceRecordsByAdmin(schoolYear, semester, studentID, dsns);
            const sortedAbsenceRecords = sortAbsenceRecordsByPeriodNames(absenceRecords, periodName);

            return [key, sortedAbsenceRecords] as const;
          })
        );

        const absenceRecordMap = new Map<string, AbsenceRecordInfo[]>(absenceRecordEntries);

        const leaveApplicationWithAbsence = LeaveApplicationList.map((item: LeaveApplicationRecord) => {
          const sortedLeaveDateAndPeriod = sortLeaveDateAndPeriodByPeriodNames(item.leaveDateAndPeriod, periodName);
          const studentAbsenceMatchInfo = item.leaveStudents.map((student) => {
            const compareRequired = shouldCompareAbsence && item.status === 'finished';

            if (!compareRequired) {
              return {
                studentID: student.studentID,
                absenceRecords: [],
                matchedAbsenceRecords: [],
                isMatched: false,
                compareRequired,
              };
            }

            const cacheKey = `${item.school_year}_${item.semester}_${student.studentID}`;
            const absenceRecords = absenceRecordMap.get(cacheKey) ?? [];
            const matchedAbsenceRecords = getMatchedAbsenceRecords(sortedLeaveDateAndPeriod, absenceRecords);
            const isMatched = isAbsenceRecordMatched(sortedLeaveDateAndPeriod, item.absence_type, matchedAbsenceRecords);

            return {
              studentID: student.studentID,
              absenceRecords,
              matchedAbsenceRecords,
              isMatched,
              compareRequired,
            };
          });

          return {
            ...item,
            leaveDateAndPeriod: sortedLeaveDateAndPeriod,
            studentAbsenceMatchInfo,
            isAbsenceRecordMatched: shouldCompareAbsence && item.status === 'finished'
              ? studentAbsenceMatchInfo.every((studentAbsence) => studentAbsence.isMatched)
              : false,
          };
        });

        const schDate = await SchoolDateHelper.getDatesForAllSemester(dsns);
        setSchoolDatesDetail(schDate);

        const resort = Util.reSortLeaveApp(leaveApplicationWithAbsence);
        setLeaveAppAllList(resort);
        setLeaveAppList(resort);
        setSortParameters(prevParams => prevParams.map(param => ({
          ...param,
          sortType: 0
        })));
        setRunFilter(true);
      } finally {
        setShowLoading(false);
      }
    }
  }, [userInfo]);

  const getLeaveRecordByNo = useCallback(async (dsns: string, application_no: string) => {
    if (userInfo && dsns) {
      console.log('===開始取得請假紀錄 getLeaveRecordByNo===', { userInfo, dsns, application_no });
      setShowLoading(true);
      try {
        const LeaveApplicationList = await ManagerLeaveHelper.get_by_leave_application_no(
          application_no,
          dsns
        );

        const shouldCompareAbsence = !!userInfo?.isSystemAdmin;
        const finishedLeaveApplicationList = shouldCompareAbsence
          ? LeaveApplicationList.filter((item: LeaveApplicationRecord) => item.status === 'finished')
          : [];

        const absenceRequestKeys = Array.from(new Set(
          finishedLeaveApplicationList.flatMap((item: LeaveApplicationRecord) => (
            item.leaveStudents.map((student) => `${item.school_year}_${item.semester}_${student.studentID}`)
          ))
        ));

        // 取得學校允許請假的節次，用於排序請假節次與缺曠節次
        const periodName = await SchoolPeriodHelper.getAllowedPeriods(undefined, dsns);

        const absenceRecordEntries = await Promise.all(
          absenceRequestKeys.map(async (key) => {
            const [schoolYear, semester, studentID] = key.split('_').map(Number);
            const absenceRecords = await DSAAbsenceHelper.getAbsenceRecordsByAdmin(schoolYear, semester, studentID, dsns);
            const sortedAbsenceRecords = sortAbsenceRecordsByPeriodNames(absenceRecords, periodName);

            return [key, sortedAbsenceRecords] as const;
          })
        );

        const absenceRecordMap = new Map<string, AbsenceRecordInfo[]>(absenceRecordEntries);

        const leaveApplicationWithAbsence = LeaveApplicationList.map((item: LeaveApplicationRecord) => {
          const sortedLeaveDateAndPeriod = sortLeaveDateAndPeriodByPeriodNames(item.leaveDateAndPeriod, periodName);
          const studentAbsenceMatchInfo = item.leaveStudents.map((student) => {
            const compareRequired = shouldCompareAbsence && item.status === 'finished';

            if (!compareRequired) {
              return {
                studentID: student.studentID,
                absenceRecords: [],
                matchedAbsenceRecords: [],
                isMatched: false,
                compareRequired,
              };
            }

            const cacheKey = `${item.school_year}_${item.semester}_${student.studentID}`;
            const absenceRecords = absenceRecordMap.get(cacheKey) ?? [];
            const matchedAbsenceRecords = getMatchedAbsenceRecords(sortedLeaveDateAndPeriod, absenceRecords);
            const isMatched = isAbsenceRecordMatched(sortedLeaveDateAndPeriod, item.absence_type, matchedAbsenceRecords);

            return {
              studentID: student.studentID,
              absenceRecords,
              matchedAbsenceRecords,
              isMatched,
              compareRequired,
            };
          });

          return {
            ...item,
            leaveDateAndPeriod: sortedLeaveDateAndPeriod,
            studentAbsenceMatchInfo,
            isAbsenceRecordMatched: shouldCompareAbsence && item.status === 'finished'
              ? studentAbsenceMatchInfo.every((studentAbsence) => studentAbsence.isMatched)
              : false,
          };
        });

        const schDate = await SchoolDateHelper.getDatesForAllSemester(dsns);
        setSchoolDatesDetail(schDate);

        const resort = Util.reSortLeaveApp(leaveApplicationWithAbsence);
        setLeaveAppAllList(resort);
        setLeaveAppList(resort);
        setSortParameters(prevParams => prevParams.map(param => ({
          ...param,
          sortType: 0
        })));
        setRunFilter(true);
      } finally {
        setShowLoading(false);
      }
    }
  }, [userInfo]);

  const exportLeaveRecord = async () => {
    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet(`請假紀錄-${dayjs(startDate).format('YYYYMMDD')}-${dayjs(endDate).format('YYYYMMDD')}`);
    const isSystemAdmin = !!userInfo?.isSystemAdmin;
    const columns = [
      { header: '序號', key: 'index', width: 10 },
      { header: '年級', key: 'gradeYear', width: 10 },
      { header: '班級', key: 'className', width: 10 },
      { header: '座號', key: 'seatNo', width: 10 },
      { header: '學號', key: 'studentNumber', width: 15 },
      { header: '姓名', key: 'name', width: 15 },
      { header: '假單狀態', key: 'status', width: 20 },
      { header: '請假類型', key: 'absence_type', width: 10 },
      { header: '請假日期', key: 'leaveDate', width: 15 },
      { header: '節次', key: 'periods', width: 45 },
      { header: '事由', key: 'reason', width: 30 },
      { header: '附件', key: 'has_attachment', width: 10 },
      { header: '申請時間', key: 'apply_time', width: 20 },
      { header: '核准狀態', key: 'approve_status', width: 20 },
      { header: '核准時間', key: 'approve_time', width: 20 },
      { header: '核准人', key: 'approver', width: 20 },
      { header: '核准意見', key: 'comment', width: 30 },
    ];

    if (isSystemAdmin) {
      columns.splice(11, 0,
        { header: '校務缺曠節次(假別)', key: 'schoolAbsenceDetail', width: 55 },
        { header: '缺曠比對', key: 'absenceComparison', width: 10 }
      );
    }

    sheet.columns = columns;

    const columnIndexMap = new Map(
      columns.map((column, index) => [column.key, index + 1])
    );

    let currentRow = 2; // Start from the first row after the header.
    let idx = 1;
    console.log(LeaveAppList);
    for (const lapp of LeaveAppList) {
      const maxRows = Math.max(lapp.leaveDateAndPeriod.length, lapp.leaveStudents.length, 1);
      const primaryLeaveStudent = lapp.leaveStudents[0];
      const statusStr: {
        progressText: string;
        progressTextCSS: string;
        statusText: string;
        statusTextCSS: string;
        status: string;
      } = Util.calculateProgressText(lapp);
      const approveHistorySummary = buildApproveHistorySummary(lapp.approveHistory);

      for (let i = 0; i < maxRows; i++) {
        const leaveDateAndPeriod = lapp.leaveDateAndPeriod[i];
        const leaveStudent = lapp.leaveStudents.length <= 1
          ? primaryLeaveStudent
          : lapp.leaveStudents[i];
        const studentAbsenceTarget = leaveStudent ?? primaryLeaveStudent;
        const studentAbsenceMatchInfo = studentAbsenceTarget
          ? getStudentAbsenceMatchInfo(lapp, studentAbsenceTarget.studentID)
          : undefined;
        const exportAbsenceInfo = getExportAbsenceInfo(
          leaveDateAndPeriod,
          lapp.absence_type,
          studentAbsenceMatchInfo
        );

        // Add a row for each student.
        sheet.addRow({
          index: idx,
          gradeYear: leaveStudent?.gradeYear,
          className: leaveStudent?.className,
          seatNo: leaveStudent?.seatNo,
          studentNumber: leaveStudent?.studentNumber,
          name: leaveStudent?.name,
          status: (statusStr.progressText === '申請中') ? '審核中' : statusStr.progressText,
          absence_type: lapp.absence_type,
          leaveDate: leaveDateAndPeriod?.d ? dayjs(leaveDateAndPeriod.d).format('YYYY/MM/DD') : '',
          periods: leaveDateAndPeriod?.periods?.join(',') ?? '',
          reason: lapp.reason,
          schoolAbsenceDetail: isSystemAdmin ? exportAbsenceInfo.schoolAbsenceDetail : undefined,
          absenceComparison: isSystemAdmin ? exportAbsenceInfo.absenceComparison : undefined,
          has_attachment: lapp.attachment_info ? '有' : '無',
          apply_time: dayjs(lapp.apply_time).format('YYYY/MM/DD HH:mm:ss'),
          approve_status: approveHistorySummary.approve_status,
          approve_time: approveHistorySummary.approve_time,
          approver: approveHistorySummary.approver,
          comment: approveHistorySummary.comment,
        });
      }
      if (maxRows > 1) {
        const mergeStartRow = currentRow;
        const mergeColumns = [
          'index',
          'gradeYear',
          'className',
          'seatNo',
          'studentNumber',
          'name',
          'status',
          'absence_type',
          'reason',
          'has_attachment',
          'apply_time',
          'approve_status',
          'approve_time',
          'approver',
          'comment'
        ];

        mergeColumns.forEach((key) => {
          const columnIndex = columnIndexMap.get(key);

          if (!columnIndex) {
            return;
          }

          const col = getExcelColumnName(columnIndex);
          sheet.mergeCells(`${col}${mergeStartRow}:${col}${mergeStartRow + maxRows - 1}`);
        });
      }
      idx++;
      currentRow += maxRows;
    }

    const periodsColumnIndex = columnIndexMap.get('periods');
    const reasonColumnIndex = columnIndexMap.get('reason');
    const commentColumnIndex = columnIndexMap.get('comment');
    const absenceComparisonColumnIndex = columnIndexMap.get('absenceComparison');

    sheet.eachRow(function (row, rowNumber) {
      row.eachCell(function (cell, colNumber) {
        const shouldAlignLeft = colNumber === periodsColumnIndex
          || colNumber === reasonColumnIndex
          || colNumber === commentColumnIndex;

        if (shouldAlignLeft && rowNumber > 1) {
          cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
        }
        else {
          cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
        }

        if (rowNumber > 1 && colNumber === absenceComparisonColumnIndex) {
          if (cell.value === '一致') {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFE6FFFA' }
            };
            cell.font = {
              color: { argb: 'FF1C7C54' },
              bold: true
            };
          }

          if (cell.value === '不一致') {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFE6E6' }
            };
            cell.font = {
              color: { argb: 'FFB42318' },
              bold: true
            };
          }
        }
      });
    });
    await workbook.xlsx.writeBuffer().then(buffer => {
      FileSaver.saveAs(new Blob([buffer]), `請假紀錄-${dayjs().format('YYYY/MM/DD HH:mm:ss')}.xlsx`);
    });

  }

  const setBadge = (status: string) => {
    switch (status) {
      case 'progressing':
        return <Badge borderRadius='4' px='2' w='52px' colorScheme='green'><Center>審核中</Center></Badge>;
      case 'finished':
        return <Badge borderRadius='4' px='2' w='52px' colorScheme='blue'><Center>已簽核</Center></Badge>;
      case 'rejected':
        return <Badge borderRadius='4' px='2' w='52px' colorScheme='red'><Center>已退回</Center></Badge>;
      case 'canceled':
        return <Badge borderRadius='4' px='2' w='52px' colorScheme='purple'><Center>已抽單</Center></Badge>;
      default:
        return <Badge borderRadius='4' px='2' w='52px' colorScheme='gray'><Center></Center></Badge>;
    }
  }
  useEffect(() => {
    if (runFilter) {
      let temp = LeaveAppAllList.filter((item) => {
        return (statusArr[0] && item.status === 'progressing') ||
          (statusArr[1] && item.status === 'finished') ||
          (statusArr[2] && item.status === 'rejected') ||
          (statusArr[3] && item.status === 'canceled');
      });
      temp = temp.filter((item) => {
        const lastLeaveDate = item.leaveDateAndPeriod
          .map(lp => new Date(lp.d))
          .sort((a, b) => b.getTime() - a.getTime())[0];

        const applyTime = new Date(item.apply_time);
        const applyDateOnly = new Date(applyTime.getFullYear(), applyTime.getMonth(), applyTime.getDate());
        const leaveDateOnly = new Date(lastLeaveDate.getFullYear(), lastLeaveDate.getMonth(), lastLeaveDate.getDate());

        const applyTimeMs = applyDateOnly.getTime();
        const leaveTimeMs = leaveDateOnly.getTime();

        // 找出對應的 schoolDates
        const matchedSemesterSchoolDate = schoolDatesDetail.find(
          sd => sd.school_year === item.school_year && sd.semester === item.semester
        );

        // console.log(item.id, { item, matchedSemesterSchoolDate });
        let leaveToApplySchoolDays = 0;
        if (matchedSemesterSchoolDate) {
          leaveToApplySchoolDays = matchedSemesterSchoolDate.dates.filter(s => {
            const schoolDate = new Date(s.d);
            const schoolDateOnly = new Date(schoolDate.getFullYear(), schoolDate.getMonth(), schoolDate.getDate());
            const ms = schoolDateOnly.getTime();
            return ms > leaveTimeMs && ms <= applyTimeMs;
          }).length;
        }

        // const daysDiff = (applyDateOnly.getTime() - leaveDateOnly.getTime()) / (1000 * 60 * 60 * 24);
        // console.log(item.id, daysDiff);

        return ((gradeYear === 0) || (item.leaveStudents[0].gradeYear === gradeYear)) &&
          ((currentClass === 0) || (item.leaveStudents[0].classID === currentClass)) &&
          ((keyword === '') || (item.leaveStudents[0].name.indexOf(keyword) > -1) || (item.leaveStudents[0].studentNumber.indexOf(keyword) > -1)) &&
          ((keyDays === null ? true : leaveToApplySchoolDays >= keyDays))
          ;
      });
      temp.sort((a, b) => {
        let result = 0;
        if (sortParameters[0].sortType !== 0) {
          if (a.leaveStudents[0].gradeYear < b.leaveStudents[0].gradeYear) result = sortParameters[0].sortType;
          if (a.leaveStudents[0].gradeYear > b.leaveStudents[0].gradeYear) result = -sortParameters[0].sortType;
          if (result !== 0) return result;
        }
        if (sortParameters[1].sortType !== 0) {
          if (a.leaveStudents[0].className < b.leaveStudents[0].className) result = sortParameters[1].sortType;
          if (a.leaveStudents[0].className > b.leaveStudents[0].className) result = -sortParameters[1].sortType;
          if (result !== 0) return result;
        }
        if (sortParameters[2].sortType !== 0) {
          if (a.leaveStudents[0].studentNumber < b.leaveStudents[0].studentNumber) result = sortParameters[2].sortType;
          if (a.leaveStudents[0].studentNumber > b.leaveStudents[0].studentNumber) result = -sortParameters[2].sortType;
          if (result !== 0) return result;
        }
        if (sortParameters[3].sortType !== 0) {
          if (a.leaveStudents[0].name < b.leaveStudents[0].name) result = sortParameters[3].sortType;
          if (a.leaveStudents[0].name > b.leaveStudents[0].name) result = -sortParameters[3].sortType;
          if (result !== 0) return result;
        }
        if (sortParameters[4].sortType !== 0) {
          if (a.leaveDateAndPeriod[0].d < b.leaveDateAndPeriod[0].d) result = sortParameters[4].sortType;
          if (a.leaveDateAndPeriod[0].d > b.leaveDateAndPeriod[0].d) result = -sortParameters[4].sortType;
          if (result !== 0) return result;
        }
        return result;
      });
      setLeaveAppList(temp);
      setRunFilter(false);
      setDownloadEnable(temp.length > 0);
    }
  }, [LeaveAppAllList, currentClass, gradeYear, keyDays, keyword, runFilter, schoolDatesDetail, sortParameters, statusArr]);

  const getAllClassesByDsns = useCallback(async (dsns: string) => {
    const res = await DSAAbsenceHelper.getSchoolClasses(dsns);
    const rawClasses = res.class || [];

    // 1. 提取所有 gradeYear 並透過 Set 去重
    const uniqueGrades = Array.from(
      new Set(
        rawClasses
          .map((item: any) => item.gradeYear)
          .filter((g: any) => g !== null && g !== undefined && g !== '') // 過濾無效值
      )
    ).sort((a: any, b: any) => Number(a) - Number(b)); // 依年級排序

    setAllClasses(rawClasses);
    setGradeYears(uniqueGrades as number[]);
  }, []);

  useEffect(() => {
    queryDateRangeRef.current = { startDate, endDate };
  }, [endDate, startDate]);

  useEffect(() => {
    if (!selectedDsns) {
      return;
    }

    void getLeaveRecord(selectedDsns, queryDateRangeRef.current.startDate, queryDateRangeRef.current.endDate);
    void getAllClassesByDsns(selectedDsns);
    setGradeClass([]);
    setCurrentClass(0);
    setGradeYear(0);
  }, [getAllClassesByDsns, getLeaveRecord, selectedDsns]);


  return (
    <>
      <Box borderWidth='1px' borderRadius='lg' p="6" h='87vh' overflow='hidden'>
        <div className="h-full overflow-y-auto">
          <div className="mb-5">
            <div className="flex items-center gap-3">
              <div>請假日期：</div>
              <div>
                <InputGroup className="items-center">
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    // showIcon
                    // toggleCalendarOnIconClick
                    locale="zhTW"
                    calendarStartDay={0}
                    shouldCloseOnSelect={false}
                    className="z-10 relative bg-transparent"
                    selected={startDate}
                    onChange={(date: Date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                  />
                  <CalendarIcon ml={2} color={color} className="p-2 absolute right-0" style={{ boxSizing: 'content-box' }} />
                </InputGroup>
              </div>
              <div>至</div>
              <div>
                <InputGroup className="items-center">
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    locale="zhTW"
                    calendarStartDay={0}
                    shouldCloseOnSelect={false}
                    className="z-10 relative bg-transparent"
                    selected={endDate}
                    onChange={(date: Date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                  />
                  <CalendarIcon ml={2} color={color} className="p-2 absolute right-0" style={{ boxSizing: 'content-box' }} />

                  {/* <DatePicker
                    locale={taiwan_zh}  
                  />
                  <InputRightElement>
                    <DatePickerIcon />
                  </InputRightElement> */}
                </InputGroup>
              </div>
              <div className="flex ms-4 gap-2">
                <Button onClick={() => getLeaveRecord(selectedSchool?.dsns ?? '', startDate, endDate)} rightIcon={<Search2Icon />} colorScheme='cyan' variant='outline' >
                  搜尋
                </Button>

                <div className="flex items-center gap-2">
                  <InputGroup w='200px'>
                    <Input
                      name="search_leave_app_id"   // 1. 給它一個明確的 name，避開 card/cc 等關鍵字
                      autoComplete="off"           // 2. 關閉瀏覽器自動填入
                      data-lpignore="true"
                      placeholder='請假單號'
                      value={leaveAppNo}
                      onChange={(e) => setLeaveAppNo(e.target.value)}
                    />
                  </InputGroup>
                  <Button
                    onClick={() => getLeaveRecordByNo(selectedSchool?.dsns ?? '', leaveAppNo)}
                    colorScheme='teal'
                    rightIcon={<Search2Icon />}
                  >
                    單號搜尋
                  </Button>
                </div>

                <Button onClick={exportLeaveRecord} rightIcon={<DownloadIcon />} colorScheme='cyan' variant='outline' style={{ marginRight: "10px" }} isDisabled={!donwloadEnable || showLoading}>
                  下載
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4 mb-5">
              <div>篩選：</div>
              {/* <Select placeholder='年級' w='104px' value={gradeYear}  onChange={handleGradeYear()}> */}
              <Select w='104px' value={gradeYear} onChange={handleGradeYear}>
                <option value='0'>年級</option>
                {gradeYears.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}年級
                  </option>
                ))}
              </Select>

              <Select w='104px' value={currentClass} onChange={handleCurrentClass()}>
                <option value='0'>班級</option>
                {
                  (gradeClass.length > 0) ? gradeClass.map((item) => {
                    return (
                      <option value={item.classID}>{item.className}</option>
                    )
                  }) : <> </>
                }
              </Select>
              <div className="">或</div>
              <InputGroup w='200px'>
                <Input placeholder='學生姓名或學號' onChange={handleKeyword()} />
                <InputRightElement>
                  <Search2Icon color='gray.400' />
                </InputRightElement>
              </InputGroup>
              <div className="">或</div>
              <InputGroup w='240px'>
                <Input placeholder='申請日晚於請假日 X 天' onChange={handleKeyDays()}
                  value={keyDays ?? ''} inputMode="numeric" pattern="\d*" />
                <InputRightElement>
                  <Search2Icon color='gray.400' />
                </InputRightElement>
              </InputGroup>
            </div>

            <div className="flex items-center gap-3">
              <div>審核狀態：</div>
              <Stack spacing={5} direction='row'>
                <Checkbox isChecked={statusArr[0]} colorScheme='cyan' onChange={(e) => handleStatus(0, e.target.checked)}>審核中</Checkbox>
                <Checkbox isChecked={statusArr[1]} colorScheme='cyan' onChange={(e) => handleStatus(1, e.target.checked)}>已簽核</Checkbox>
                <Checkbox isChecked={statusArr[2]} colorScheme='cyan' onChange={(e) => handleStatus(2, e.target.checked)}>已退回</Checkbox>
                <Checkbox isChecked={statusArr[3]} colorScheme='cyan' onChange={(e) => handleStatus(3, e.target.checked)}>已抽單</Checkbox>
              </Stack>
            </div>
          </div>
          <TableContainer overflowX='auto' overflowY='auto' w='100%'>
            <Table size='lg' variant='striped'>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th onClick={() => updateSortParameters(0)}>{(sortParameters[0].sortType === 0) ? <UpDownIcon /> : (sortParameters[0].sortType === 1) ? <TriangleDownIcon /> : <TriangleUpIcon />}年級</Th>
                  <Th onClick={() => updateSortParameters(1)}>{(sortParameters[1].sortType === 0) ? <UpDownIcon /> : (sortParameters[1].sortType === 1) ? <TriangleDownIcon /> : <TriangleUpIcon />}班級</Th>
                  <Th onClick={() => updateSortParameters(2)}>{(sortParameters[2].sortType === 0) ? <UpDownIcon /> : (sortParameters[2].sortType === 1) ? <TriangleDownIcon /> : <TriangleUpIcon />}學號</Th>
                  <Th onClick={() => updateSortParameters(3)}>{(sortParameters[3].sortType === 0) ? <UpDownIcon /> : (sortParameters[3].sortType === 1) ? <TriangleDownIcon /> : <TriangleUpIcon />}姓名</Th>
                  <Th>假單狀態</Th>
                  <Th onClick={() => updateSortParameters(4)}>{(sortParameters[4].sortType === 0) ? <UpDownIcon /> : (sortParameters[4].sortType === 1) ? <TriangleDownIcon /> : <TriangleUpIcon />}請假日期</Th>
                  {
                    (userInfo?.isSystemAdmin) &&
                    <Th>缺曠比對</Th>
                  }
                  <Th>查看更多</Th>
                </Tr>
              </Thead>
              <Tbody className="cursor-pointer">
                {
                  (() => {
                    let currCnt = 1;
                    return LeaveAppList.map((item, index) =>
                      item.leaveStudents.map((student, stuIdx) => {
                        const absenceMatchInfo = getStudentAbsenceMatchInfo(item, student.studentID);

                        return (
                          <Tr key={`${index}-${stuIdx}`}>
                            <Td>{currCnt++}</Td>
                            <Td>{student.gradeYear}</Td>
                            <Td isNumeric>{student.className}</Td>
                            <Td isNumeric>{student.studentNumber}</Td>
                            <Td>{student.name}</Td>
                            <Td>{setBadge(item.status)}</Td>
                            <Td>{convLeaveDateStr(item.leaveDateAndPeriod)}</Td>
                            {
                              (userInfo?.isSystemAdmin) &&
                              <Td>
                                {absenceMatchInfo?.compareRequired && (
                                  <Badge borderRadius='4' px='2' colorScheme={absenceMatchInfo.isMatched ? 'green' : 'red'}>
                                    <Center>{absenceMatchInfo.isMatched ? '一致' : '不一致'}</Center>
                                  </Badge>
                                )}
                              </Td>
                            }
                            <Td onClick={handleDetailOpen(item, student)}>詳細</Td>
                          </Tr>
                        )
                      })
                    )
                  }
                  )()
                }
              </Tbody>
            </Table>
          </TableContainer>
        </div>
      </Box>
      <LeaveAppModal selectedLeaveApp={selectedLeaveApp} selectedLeaveStu={selectedLeaveStu} isModalOpen={isModalOpen} onModalClose={onModalClose} isSystemAdmin={userInfo?.isSystemAdmin} />
      <LoadingDialog isOpen={showLoading} />
    </>
  )
}